import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import FlowGuard from '../../../shared/components/FlowGuard';

interface CompanyAdminWelcomeContentProps {
    userName: string;
    companyName: string;
}

const CompanyAdminWelcomeContent: React.FC<CompanyAdminWelcomeContentProps> = ({ userName, companyName }) => {
    const navigate = useNavigate();

    const handleInviteTeam = () => {
        navigate('/companies/invite-team', {
            state: {
                companyName: companyName
            }
        });
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Welcome Message */}
            <div className="w-full lg:flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="mb-12">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            onClick={() => navigate('/')}
                            className="h-12 w-auto cursor-pointer"
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '48px',
                            lineHeight: '100%',
                            color: '#015FA6',
                            marginBottom: '24px'
                        }}
                    >
                        Welcome aboard, {userName}!
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 300,
                            fontSize: '18px',
                            lineHeight: '150%',
                            color: '#015FA6',
                            marginBottom: '72px'
                        }}
                    >
                        You're now the company admin for {companyName}.
                    </p>

                    {/* Invite Team Button */}
                    <button
                        onClick={handleInviteTeam}
                        style={{
                            width: '100%',
                            maxWidth: '529px',
                            height: '55px',
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '20px',
                            lineHeight: '28px',
                            color: '#F7FAFC',
                            background: '#015FA6',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#014580';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#015FA6';
                        }}
                    >
                        Invite Team Members
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M5 12h14m-7-7l7 7-7 7"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div className="hidden lg:block lg:flex-1">
                <AuthHero variant="company" />
            </div>
        </div>
    );
};

/**
 * CompanyAdminWelcome Page
 * Protected by FlowGuard - only accessible after company setup flow
 * Direct URL access redirects to company signup page
 */
const CompanyAdminWelcome: React.FC = () => {
    const location = useLocation();

    // Get user info from navigation state or localStorage
    const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
    const userName = location.state?.firstName || authUser.first_name || 'there';
    const companyName = location.state?.companyName || authUser.company_name || 'Hush Company';

    return (
        <FlowGuard
            requiredStateKey="fromCompanySetup"
            fallbackPath="/auth/company/signup"
            alternateStateKeys={["fromCompanySignup"]}
        >
            <CompanyAdminWelcomeContent userName={userName} companyName={companyName} />
        </FlowGuard>
    );
};

export default CompanyAdminWelcome;
